import React, { useRef, useState } from "react";
import { useCurrent, useDelete, useFetch } from "../../hooks";
import { MwSpinner, MwButton, NoDataFound } from "../ui";
import { useParams, useNavigate } from "react-router-dom";
import { FaTimes, FaFilePdf, FaPrint, FaFileExcel } from "react-icons/fa";
import handleConvertToPDF from "../../utils/handleConvertToPDF";
import DeleteConfirmationModal from "./RequisitionComponents/DeleteConfirmationModal";
import { MdDelete, MdEditNote, MdInventory2 } from "react-icons/md";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import CardHeadeWithActions from "./../ui/CardHeadeWithActions";
import { FaRegFileExcel } from "react-icons/fa";

const RequisitionTable = () => {
  const [t] = useTranslation("global");

  const { requisitionid } = useParams();
  const { tenant } = useParams();
  const navigate = useNavigate();
  const [menuVisible, setMenuVisible] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedUnitId, setSelectedUnitId] = useState(null);

  const { currentTenantId, currentCompanyId, currentBranchId } = useCurrent();
  const {
    deleteItem,
    loading: deleteLoading,
    error: deleteError,
  } = useDelete();

  const printRef = useRef();

  const requisitionUrl = `${process.env.REACT_APP_INVENTORY_API_SERVER_BASE_URL}/requisitions/master/${requisitionid}`;
  const warehousesUrl = `${process.env.REACT_APP_INVENTORY_API_SERVER_BASE_URL}/warehouses/${currentTenantId}/${currentCompanyId}/${currentBranchId}`;

  const {
    data: requisitionData,
    loading: requisitionLoading,
    error: requisitionError,
  } = useFetch(requisitionUrl);
  const {
    data: warehousesData,
    loading: warehousesLoading,
    error: warehousesError,
  } = useFetch(warehousesUrl);

  const handleConfirmDelete = async () => {
    if (selectedUnitId) {
      try {
        const deleteUrl = `${process.env.REACT_APP_INVENTORY_API_SERVER_BASE_URL}/requisitions/master/${selectedUnitId}`;
        await deleteItem(deleteUrl);
        navigate(`/${tenant}/requisition`);
      } catch (error) {
      } finally {
        setDeleteModalOpen(false);
        setSelectedUnitId(null);
        setMenuVisible(null);
      }
    }
  };

  const handleDelete = (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedUnitId(id);
    setDeleteModalOpen(true);
  };
  // وظيفة الطباعة
  const handlePrint = () => {
    const printContents =
      document.getElementById("printable-content").outerHTML;

    // فتح نافذة جديدة
    const printWindow = window.open("", "_blank");
    printWindow.document.open();

    printWindow.document.write(`
      <!DOCTYPE html>
      <html dir="rtl" lang="ar">
        <head>
          <title>إذن مخزني</title>
          <style>
            /* إعدادات الصفحة للطباعة */
            @page {
              size: A4;
              margin: 1.5cm;
            }
            body {
              font-family: 'Arial', sans-serif;
              margin: 0;
              padding: 0;
              direction: rtl;
              text-align: right;
              color: #000;
              print-color-adjust: exact;
              -webkit-print-color-adjust: exact;
            }
             ${Array.from(document.styleSheets)
               .map((sheet) => {
                 try {
                   return Array.from(sheet.cssRules)
                     .map((rule) => rule.cssText)
                     .join("");
                 } catch (e) {
                   return "";
                 }
               })
               .join("")}
          </style>
        </head>
        <body>
          ${printContents}
          <script>
            window.onload = function () {
              window.print();
              window.onafterprint = function () {
                window.close();
              };
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  if (requisitionLoading || warehousesLoading) {
    return (
      <div className="flex justify-center items-center h-32">
        <MwSpinner />
      </div>
    );
  }

  if (requisitionError || warehousesError) {
    return (
      <div className="text-white p-4 bg-red-500 rounded-lg text-center">
        حدث خطأ أثناء جلب البيانات:{" "}
        {requisitionError?.message || warehousesError?.message}
      </div>
    );
  }

  if (!requisitionData?.data || !warehousesData?.data) {
    return <NoDataFound msg={t("stocktaking.no_data_available")} />;
  }

  const requisition = requisitionData.data;
  const warehouses = warehousesData.data;

  const handleCancel = () => {
    navigate(`/${tenant}/requisition`);
  };

  const handleUpdate = () => {
    navigate(`/${tenant}/requisition-update/${requisitionid}`);
  };

  const exportExcel = () => {
    if (
      requisitionData?.data?.details &&
      requisitionData.data.details.length > 0
    ) {
      const exportData = requisitionData.data.details.map((item) => ({
        [t("requisition.item")]: item.product_name || "",
        [t("requisition.unit_price")]: item.total
          ? (Number(item.total) / Number(item.qty)).toFixed(2)
          : "",
        [t("requisition.quantity")]: item.qty || 0,
        [t("requisition.balance_before")]: item.balance_before || 0,
        [t("requisition.balance_after")]: item.balance_after || 0,
        [t("requisition.total")]: Number(item.total || 0).toFixed(2),
      }));

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);
      XLSX.utils.book_append_sheet(wb, ws, t("reports.Warehouse_Report"));
      XLSX.writeFile(wb, `${t("reports.Warehouse_Report")}.xlsx`);
    }
  };
  return (
    <div className=" rounded-lg  bg-gray-50 print:p-0  h-[69vh]">
      <CardHeadeWithActions
        title={
          <div className="flex items-center gap-2   ">
            <MdInventory2 className="w-6 h-6" />
            <span>{t("requisition.requisition_management")}</span>
          </div>
        }
      >
        <MwButton
          actionType="button"
          inGroup={true}
          type="saveBtn"
          onClick={handleCancel}
        >
          {t("requisition.requisition_list")}
        </MwButton>
      </CardHeadeWithActions>
      <div
        className="bg-white my-3  shadow-sm rounded-lg
 "
      >
        <div className="">
          <div className="mb-6 w-full flex justify-between items-center ">
            {/* مجموعة أدوات التصدير */}
            <div className="flex items-center gap-1">
              <div className="px-2 text-[#251658]"></div>
              <div className="p-3 text-[#251658]">
                <ul className="flex gap-1">
                  <li
                    style={{ cursor: "pointer" }}
                    onClick={exportExcel}
                    className="px-2 py-1 flex items-center gap-1 bg-slate-100 hover:font-bold"
                  >
                    <FaRegFileExcel />
                    <span className="text-sm text-[#251658]">
                      {t("reports.Export_Excel")}
                    </span>
                  </li>
                  <li
                    style={{ cursor: "pointer" }}
                    onClick={() =>
                      handleConvertToPDF({
                        lang: "ar",
                        elementId: "printable-content",
                        fileName: `إذن_مخزني_${requisition.req_no}.pdf`,
                      })
                    }
                    className="px-2 py-1 flex items-center gap-1 bg-slate-100 hover:font-bold"
                  >
                    <FaFilePdf />
                    <span className="text-sm text-[#251658]">
                      {t("reports.Save_as_PDF")}
                    </span>
                  </li>
                  <li
                    style={{ cursor: "pointer" }}
                    onClick={handlePrint}
                    className="px-2 py-1 flex items-center gap-1 bg-slate-100 hover:font-bold"
                  >
                    <FaPrint />
                    <span className="text-sm text-[#251658]">
                      {t("reports.Print")}
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="flex items-center px-6 gap-2 ">
              <MwButton
                actionType="button"
                inGroup={true}
                size="sm"
                type="cancelBtn"
                onClick={(e) => handleDelete(requisitionid, e)}
              >
                <MdDelete />
                {t("requisition.delete")}
              </MwButton>

              <MwButton
                actionType="button"
                size="sm"
                inGroup={true}
                type="saveBtn"
                onClick={handleUpdate}
              >
                <MdEditNote />
                {t("requisition.edit")}
              </MwButton>
            </div>
          </div>

          <div
            id="printable-content"
            ref={printRef}
            className="p-8  print:rtl print:text-center h-[calc(100vh-300px)] overflow-y-scroll custom-scrollbar"
          >
            {/* Header */}
            <div className="text-center mb-8 border-b pb-6">
              <h1 className="text-2xl font-bold mb-2 text-[#251658]">
                {" "}
                {t("requisition.title")}{" "}
              </h1>
              <div className="text-gray-700">
                {" "}
                {t("requisition.number")} : {requisition.req_no}
              </div>
              <div className="text-gray-700">
                {t("requisition.date")}: {requisition.req_date.split(" ")[0]}
              </div>
            </div>

            {/* Warehouse Information */}
            <div className="grid grid-cols-2 gap-8 mb-8">
              <div className="space-y-2">
                <h2 className="text-lg font-semibold border-b pb-2 mb-3 text-[#251658]">
                  {" "}
                  {t("requisition.Store_Information")}
                </h2>
                <div className="text-gray-700">
                  <span className="font-medium">{t("requisition.from")}: </span>
                  {warehouses.find((w) => w.id === requisition.warehouse_id)
                    ?.name_ar || "غير محدد"}
                </div>
                {requisition.req_type === "TRANS" && (
                  <div className="text-gray-700">
                    <span className="font-medium">{t("requisition.to")}: </span>
                    {warehouses.find(
                      (w) => w.id === requisition.warehouse_id_to,
                    )?.name_ar || "غير محدد"}
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <h2 className="text-lg font-semibold border-b pb-2 mb-3 text-[#251658]">
                  {t("requisition.requisition_details")}
                </h2>
                <div className="text-gray-700">
                  <span className="font-medium">{t("requisition.type")}: </span>
                  {requisition.req_type === "TRANS"
                    ? t("requisition.type_transfer")
                    : requisition.req_type === "PUSH"
                      ? t("requisition.view_type_receive")
                      : requisition.req_type === "PULL"
                        ? t("requisition.type_issue")
                        : requisition.req_type}
                </div>
                <div className="text-gray-700">
                  <span className="font-medium">
                    {t("requisition.notes")}:{" "}
                  </span>
                  {requisition.req_note || t("requisition.no_notes")}
                </div>
              </div>
            </div>

            {/* Items Table */}
            {requisitionData?.data.length === 0 ? (
              <NoDataFound msg={t("deleted.no_data")} />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse print:text-sm text-sm">
                  <thead>
                    <tr className="bg-gray-100 text-[#000000]">
                      <th className="border p-3 text-center">
                        {t("requisition.item")}
                      </th>
                      <th className="border p-3 text-center">
                        {t("requisition.unit_price")}
                      </th>
                      <th className="border p-3 text-center">
                        {t("requisition.quantity")}
                      </th>
                      <th className="border p-3 text-center">
                        {t("requisition.balance_before")}
                      </th>
                      <th className="border p-3 text-center">
                        {t("requisition.balance_after")}
                      </th>
                      <th className="border p-3 text-center">
                        {t("requisition.total")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {requisition.details.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-100">
                        <td className="border p-3   text-gray-700">
                          {item.product_name || ""}
                        </td>
                        <td className="border p-3 text-center text-gray-700">
                          {item.total
                            ? (Number(item.total) / Number(item.qty)).toFixed(2)
                            : ""}
                        </td>
                        <td className="border p-3 text-center text-gray-700">
                          {item.qty || 0}
                        </td>
                        <td className="border p-3 text-center text-gray-700">
                          {item.balance_before || 0}
                        </td>
                        <td className="border p-3 text-center text-gray-700">
                          {item.balance_after || 0}
                        </td>
                        <td className="border p-3 text-center text-gray-700">
                          {Number(item.total || 0).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-100 font-semibold">
                      <td
                        className="border p-3 text-center text-[#251658]"
                        colSpan="5"
                      >
                        {t("requisition.total_summary")}
                      </td>
                      <td className="border p-3 text-center text-[#251658]">
                        {requisition.details
                          .reduce(
                            (sum, item) => sum + Number(item.total || 0),
                            0,
                          )
                          .toFixed(2)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            {/* Footer - Signatures */}
            <div className="mt-12 grid grid-cols-3 gap-8 text-center">
              <div className="space-y-8">
                <div className="font-semibold text-[#251658]">
                  {t("requisition.recipient")}
                </div>
                <div className="border-t pt-2 text-[#251658]">
                  {t("requisition.signature")}
                </div>
              </div>
              <div className="space-y-8">
                <div className="font-semibold text-[#251658]">
                  {" "}
                  {t("requisition.storekeeper")}{" "}
                </div>
                <div className="border-t pt-2 text-[#251658]">
                  {t("requisition.signature")}
                </div>
              </div>
              <div className="space-y-8">
                <div className="font-semibold text-[#251658]">
                  {" "}
                  {t("requisition.responsible_manager")}
                </div>
                <div className="border-t pt-2 text-[#251658]">
                  {t("requisition.signature")}
                </div>
              </div>
            </div>
          </div>
        </div>

        <DeleteConfirmationModal
          isOpen={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          onConfirm={handleConfirmDelete}
          loading={deleteLoading}
        />
        <div className="fixed bottom-10 left-1/2 transform -translate-x-1/2 w-fit max-w-full text-center bg-gray-100 py-2 px-4 rounded-md shadow-md">
          {requisition.req_attach ? (
            <a
              href={requisition.req_attach}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#251658] font-bold hover:text-[#251658] transition-colors"
            >
              {t("requisition.download_attachment")}
            </a>
          ) : (
            <p className="text-[#251658] italic">{t("requisition.no_notes")}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default RequisitionTable;
